import { Injectable, inject } from '@angular/core';
import { BudgetComponentType, DisplayedCurrency } from '@coin/shared/core/util-enums';
import { RoundOperations } from '@coin/shared/core/util-helpers';
import { firstValueFrom } from 'rxjs';
import { EmployeeWithCompensationInfo } from '../models/merit-budget-direct-with-compensation.model';
import { UpdateAllocationValuesDto } from '../models/update-allocation-values-dto.model';
import { MeritBudgetCalculationOperations } from '../operations/merit-budget-calculation.operations';
import { CurrencyConversionPipe } from '../pipes/currency-conversion.pipe';

@Injectable()
export class UpdateAllocationValuesService {
  private currencyConversion = inject(CurrencyConversionPipe);

  public async getUpdateBody(direct: EmployeeWithCompensationInfo): Promise<UpdateAllocationValuesDto> {
    const baseSalary = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.BaseSalary);
    const targetIncentiveAmount = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.TargetIncentiveAmount);
    const basePay = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.BasePay);
    const fixedAllowances = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.FixedAllowances);
    const flatAllowances = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.FlatAllowances);
    const allowancesPercentageTotal = this.getAmountToUpdateOfComponent(
      direct,
      BudgetComponentType.AllowancesPercentageInTotal
    );
    const annualPayment = this.getAmountToUpdateOfComponent(direct, BudgetComponentType.AnnualPayment);
    const { equityAmount, equityAmountInEuro } = this.getEquityAmountToUpdateOfComponent(direct);
    const totalTargetCash = this.getTTCAmountToUpdateOfComponent(direct);
    const oneTimePayment = MeritBudgetCalculationOperations.getOneTimePaymentComponent(direct)?.totalAmount;

    return {
      isDone: direct.isDone,
      simulationMemberId: direct.simulationMemberId,
      localCurrency: direct.localCurrency,
      employmentPercentage: direct.spsEligibilityEmploymentPercentage,
      equityType: MeritBudgetCalculationOperations.getEquityComponent(direct)?.equityType,
      annualPaymentFactor: MeritBudgetCalculationOperations.getBudgetComponentByType(
        direct,
        BudgetComponentType.AnnualPayment
      )?.referenceTotalValue,
      targetIncentiveAmount,
      totalTargetCash,
      oneTimePayment,
      baseSalary,
      basePay,
      fixedAllowances,
      flatAllowances,
      allowancesPercentageTotal,
      annualPayment,
      equityAmount,
      equityAmountInEuro,
      allowancesPoB: MeritBudgetCalculationOperations.getBudgetComponentByType(
        direct,
        BudgetComponentType.AllowancesPercentageInTotal
      )?.percentageOfBase,
      targetIncentiveAmountPoB: MeritBudgetCalculationOperations.getBudgetComponentByType(
        direct,
        BudgetComponentType.TargetIncentiveAmount
      )?.percentageOfBase,
      baseSalaryInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          baseSalary,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      basePayInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          basePay,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      targetIncentiveAmountInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          targetIncentiveAmount,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      fixedAllowancesInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          fixedAllowances,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      totalTargetCashInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          totalTargetCash,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      flatAllowancesInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          flatAllowances,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      allowancesPercentageTotalInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          allowancesPercentageTotal,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      annualPaymentInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          annualPayment,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      ),
      oneTimePaymentInEuro: await firstValueFrom(
        this.currencyConversion.transform(
          oneTimePayment,
          direct.localCurrency,
          false,
          DisplayedCurrency.EUR,
          undefined,
          undefined,
          undefined,
          true
        )
      )
    };
  }

  getAmountToUpdateOfComponent(
    direct: EmployeeWithCompensationInfo,
    meritBudgetComponentType: BudgetComponentType
  ): number {
    return direct?.isMeritExcluded
      ? MeritBudgetCalculationOperations.getBudgetComponentByType(direct, meritBudgetComponentType)?.currentAmount
      : MeritBudgetCalculationOperations.getBudgetComponentByType(direct, meritBudgetComponentType)?.totalAmount;
  }

  getTTCAmountToUpdateOfComponent(direct: EmployeeWithCompensationInfo): number {
    const comp = MeritBudgetCalculationOperations.getTotalTargetCashComponent(direct);
    const rounded = RoundOperations.round(direct?.isMeritExcluded ? comp?.currentAmount : comp?.totalAmount, 2);

    return Math.max(comp?.currentAmount, rounded);
  }

  getEquityAmountToUpdateOfComponent(direct: EmployeeWithCompensationInfo): {
    equityAmount: number;
    equityAmountInEuro: number;
  } {
    const component = MeritBudgetCalculationOperations.getEquityComponent(direct);
    return {
      equityAmountInEuro: RoundOperations.round(component?.totalAmount, 2),
      equityAmount: RoundOperations.round(component?.localTotalAmount, 2)
    };
  }
}
